import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Globe, Loader2 } from 'lucide-react'

import { timezoneLabel } from '@/components/shared/timezone-select'

type Notice =
  | { kind: 'resolving' }
  | { kind: 'updated'; from: string; to: string }
  | { kind: 'matches'; timezone: string }
  | { kind: 'not-found' }

interface LocationTimezoneSyncProps {
  /** The event's location as currently typed. */
  location: string
  /** The event's timezone as currently selected ('' when unset). */
  timezone: string
  onTimezoneChange: (timezone: string) => void
  /** Resolves a location to an IANA zone, or null when it can't be found. */
  resolve: (location: string) => Promise<string | null>
  /** Wait this long after the last edit before looking the location up. */
  debounceMs?: number
}

function sameLocation(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Keeps an event sheet's timezone in step with its location.
 *
 * Render it inside the sheet body (which mounts on open): the location at
 * mount is the baseline, and only a change from it triggers a lookup, so
 * opening a sheet never silently rewrites a saved zone. When the location
 * changes, the zone is looked up after typing settles and applied, and a
 * notice under the location says so with an Undo. An event that has a location
 * but no zone at all is filled on open. A location that can't be resolved
 * leaves the zone alone and says so, so the user knows to check it.
 */
export function LocationTimezoneSync({
  location,
  timezone,
  onTimezoneChange,
  resolve,
  debounceMs = 900,
}: LocationTimezoneSyncProps) {
  const trimmed = location.trim()
  const [notice, setNotice] = useState<Notice | null>(null)

  const baselineRef = useRef(trimmed)
  const fillOnOpenRef = useRef(!timezone && trimmed !== '')
  const requestRef = useRef(0)
  const latestRef = useRef({ timezone, onTimezoneChange, resolve })
  latestRef.current = { timezone, onTimezoneChange, resolve }

  useEffect(() => {
    const request = ++requestRef.current
    const fillOnOpen = fillOnOpenRef.current

    if (!fillOnOpen && sameLocation(trimmed, baselineRef.current)) {
      setNotice((current) => (current?.kind === 'resolving' ? null : current))
      return
    }
    if (!trimmed) {
      baselineRef.current = trimmed
      setNotice(null)
      return
    }

    const timer = setTimeout(async () => {
      fillOnOpenRef.current = false
      setNotice({ kind: 'resolving' })
      let resolved: string | null
      try {
        resolved = await latestRef.current.resolve(trimmed)
      } catch {
        resolved = null
      }
      if (request !== requestRef.current) return

      baselineRef.current = trimmed
      const current = latestRef.current.timezone
      if (!resolved) {
        setNotice({ kind: 'not-found' })
      } else if (resolved === current) {
        setNotice({ kind: 'matches', timezone: resolved })
      } else {
        latestRef.current.onTimezoneChange(resolved)
        setNotice({ kind: 'updated', from: current, to: resolved })
      }
    }, fillOnOpen ? 0 : debounceMs)

    return () => clearTimeout(timer)
  }, [trimmed, debounceMs])

  if (!notice) return null

  return (
    <div role="status" aria-live="polite" className="text-xs">
      {notice.kind === 'resolving' && (
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking this location&rsquo;s timezone…
        </p>
      )}
      {notice.kind === 'matches' && (
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <Globe className="h-3 w-3" />
          Timezone matches this location ({timezoneLabel(notice.timezone)}).
        </p>
      )}
      {notice.kind === 'updated' && (
        <p className="flex flex-wrap items-center gap-1.5 text-foreground">
          <Globe className="h-3 w-3 text-primary" />
          <span>
            Timezone {notice.from ? 'updated' : 'set'} to{' '}
            <span className="font-medium">{timezoneLabel(notice.to)}</span> to match this location.
          </span>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {
              latestRef.current.onTimezoneChange(notice.from)
              setNotice(null)
            }}
          >
            {notice.from ? `Undo (keep ${timezoneLabel(notice.from)})` : 'Undo'}
          </button>
        </p>
      )}
      {notice.kind === 'not-found' && (
        <p className="flex items-center gap-1.5 text-status-warning-fg">
          <AlertTriangle className="h-3 w-3" />
          Couldn&rsquo;t find a timezone for this location. Check the Timezone field.
        </p>
      )}
    </div>
  )
}
